import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import classes from "./ConversationsSelector.module.css";
import { useFetchUserStudiosIds } from "../../hooks/useFetchUserStudiosIds";
import { markConversationAsViewed, hasUnviewedMessages, subscribeToViewedChanges } from '../../util/messageViewedTracker';
import { getStudioImageSrc } from '../../util/studioImageUtils';
import { getConversationStatusBadge, getConversationStatusMessage } from '../../util/conversationStatus';

import fakeProfile from "../../public/fakeUser/noUser.webp"
// import { useMutation } from "@apollo/client"
// import { deleteChatRoomV2 } from "../../graphql/messaging"

const SUPPORT_LOGO_SRC = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/stu-logo.svg`;

/** Studio + partner avatars with skeleton placeholders until images finish loading. */
const ConversationItemThumbnails = ({ studioSrc, profileSrc, isSupport, classes: c }) => {
  const [studioReady, setStudioReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);

  useEffect(() => {
    setStudioReady(false);
    setProfileReady(false);
  }, [studioSrc, profileSrc]);

  const studioImgRef = (node) => {
    if (node?.complete && node.naturalWidth > 0) setStudioReady(true);
  };
  const profileImgRef = (node) => {
    if (node?.complete && node.naturalWidth > 0) setProfileReady(true);
  };

  // Support chat: show only the large Stu logo, no small partner badge.
  if (isSupport) {
    return (
      <div className={c.imageContainer}>
        <div className={c.studioThumbWrap}>
          {!studioReady && <div className={c.skeletonStudio} aria-hidden />}
          <img
            ref={studioImgRef}
            src={studioSrc}
            alt="Stu Support"
            className={c.image}
            onLoad={() => setStudioReady(true)}
            onError={() => setStudioReady(true)}
            style={{ opacity: studioReady ? 1 : 0, transition: "opacity 0.2s ease" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={c.imageContainer}>
      <div className={c.studioThumbWrap}>
        {!studioReady && <div className={c.skeletonStudio} aria-hidden />}
        <img
          ref={studioImgRef}
          src={studioSrc}
          alt="Studio"
          className={c.image}
          onLoad={() => setStudioReady(true)}
          onError={() => setStudioReady(true)}
          style={{ opacity: studioReady ? 1 : 0, transition: "opacity 0.2s ease" }}
        />
      </div>
      <div className={c.whiteCircle}>
        <div className={c.profilePicture}>
          {!profileReady && <div className={c.skeletonProfile} aria-hidden />}
          <img
            ref={profileImgRef}
            src={profileSrc}
            alt="Profil"
            onLoad={() => setProfileReady(true)}
            onError={() => setProfileReady(true)}
            style={{ opacity: profileReady ? 1 : 0, transition: "opacity 0.2s ease" }}
          />
        </div>
      </div>
    </div>
  );
};

export const ConversationsSelector = ({
  isLoading, 
  conversations, 
  selectedConversation, 
  activeConversation,
  isStudioWindowOpen,
  refetch,
  hasStudios,
  userId
  }) => { 
    
  const TAB_SWITCH_DELAY_MS = 420;
  const MAX_RENDERED_CONVERSATIONS = 40;
  const [activeTab, setActiveTab] = useState(0);
  const [appliedTab, setAppliedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [viewedUpdateTrigger, setViewedUpdateTrigger] = useState(0);
  const listItemsRef = useRef([]);
  const navRef = useRef(null);
  const conversationsScrollRef = useRef(null);
  const tabSwitchTimeoutRef = useRef(null);
  // const [deletingConversationId, setDeletingConversationId] = useState(null);
  // const [openDeleteWindows, setOpenDeleteWindows] = useState({});

  const { data: studiosIds, loading: studiosIdsLoading, error: studiosIdsError } = useFetchUserStudiosIds(userId);

  // Subscribe to viewed conversation changes to trigger re-render
  useEffect(() => {
    if (!userId) return;
    
    const unsubscribe = subscribeToViewedChanges(() => {
      setViewedUpdateTrigger(prev => prev + 1);
      // Also trigger refetch to get latest unread counts
      if (refetch) {
        setTimeout(() => refetch(), 100);
      }
    });
    
    return unsubscribe;
  }, [userId, refetch]);

  // const handleDeleteWindow = (conversationId, e) => {
  //   e.stopPropagation(); // Prevent triggering conversation selection
  //   setOpenDeleteWindows(prev => ({
  //     ...prev,
  //     [conversationId]: !prev[conversationId]
  //   }));
  // }

  // const [
  //     deleteChatRoomMutation, 
  //     { loading: deleteLoading }] = useMutation(deleteChatRoomV2, {
  //   onCompleted: () => {
  //     setDeletingConversationId(null);
  //     if (refetch) {
  //       refetch();
  //     }
  //   },
  //   onError: (error) => {
  //     console.error("Error deleting chat room:", error);
  //     setDeletingConversationId(null);
  //     const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || '';
      
  //     // Check for authorization errors - the auto-generated deleteChatRoom is blocked
  //     if (errorMessage.includes('Not Authorized') || 
  //         errorMessage.includes('Unauthorized') ||
  //         errorMessage.includes('deleteChatRoom on type Mutation')) {
  //       alert("Das Löschen von Chats ist derzeit nicht verfügbar. Die Funktion wird in Kürze hinzugefügt.");
  //     } else {
  //       alert("Fehler beim Löschen des Chats. Bitte versuche es erneut.");
  //     }
  //   }
  // });

  const handleConversationClick = (conversation) => {
    // Mark conversation as viewed when clicked
    if (conversation?.id && userId) {
      const isHost = conversation.hostID === userId;
      const unreadCount = isHost 
        ? (conversation.hostUnreadCount || 0)
        : (conversation.guestUnreadCount || 0);
      markConversationAsViewed(conversation.id, unreadCount, userId);
      // Trigger refetch to update unread counts immediately
      if (refetch) {
        setTimeout(() => refetch(), 100);
      }
    }
    selectedConversation(conversation);
  };

  useEffect(() => {
    return () => {
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current);
      }
    };
  }, []);

  const handleTabSwitch = (targetTab) => {
    setActiveTab(targetTab);

    if (tabSwitchTimeoutRef.current) {
      clearTimeout(tabSwitchTimeoutRef.current);
    }

    tabSwitchTimeoutRef.current = setTimeout(() => {
      setAppliedTab(targetTab);
      tabSwitchTimeoutRef.current = null;
    }, TAB_SWITCH_DELAY_MS);
  };

  const getFilteredConversations = useCallback(() => {
    if (!conversations || conversations.length === 0) return [];
    
    // First deduplicate by ID to prevent key issues
    const uniqueConversations = [];
    const seenIds = new Set();
    
    for (const conversation of conversations) {
      if (!seenIds.has(conversation.id)) {
        seenIds.add(conversation.id);
        uniqueConversations.push(conversation);
      }
    }
    
    let filtered = [...uniqueConversations];

    // If studios are still loading and we're on a filtered tab, return empty array
    if (studiosIdsLoading && (appliedTab === 1 || appliedTab === 2)) {
      return [];
    }

    // If there's an error loading studios and we're on a filtered tab, return empty array
    if (studiosIdsError && (appliedTab === 1 || appliedTab === 2)) {
      return [];
    }
    
    // Get user's studio IDs
    const userStudioIds = studiosIds?.studiosByUserID?.items?.map(studio => studio.id) || [];
    
    // Filter by tab type
    if (appliedTab === 1) { // Entdecker tab
      // Show conversations where the studio does NOT belong to the user
      filtered = filtered.filter(conversation => {
        return !userStudioIds.includes(conversation.studioID);
      });
    } else if (appliedTab === 2) { // Creator tab
      // Show conversations where the studio DOES belong to the user
      filtered = filtered.filter(conversation => {
        return userStudioIds.includes(conversation.studioID);
      });
    }
    // activeTab === 0 (Alle) shows all conversations, so no additional filtering needed

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conversation => {
        // Check if chat partner's name matches
        const nameMatch = conversation.chatPartner && 
          conversation.chatPartner.firstName &&
          conversation.chatPartner.firstName.toLowerCase().includes(query);
        
        // Check if last message content matches
        const messageMatch = conversation.lastMessage && 
          conversation.lastMessage.content &&
          conversation.lastMessage.content.toLowerCase().includes(query);
          
        // Check if studio name matches
        const studioMatch = conversation.studio && 
          conversation.studio.StudioName &&
          conversation.studio.StudioName.toLowerCase().includes(query);

        return nameMatch || messageMatch || studioMatch;
      });
    }

    return filtered;
  }, [
    appliedTab,
    conversations,
    searchQuery,
    studiosIds,
    studiosIdsError,
    studiosIdsLoading,
  ]);

  const filteredConversations = useMemo(() => {
    return getFilteredConversations();
  }, [getFilteredConversations]);

  const visibleConversations = useMemo(() => {
    const supportConversations = filteredConversations.filter(
      (conversation) => conversation.chatType === 'SUPPORT'
    );
    const regularConversations = filteredConversations.filter(
      (conversation) => conversation.chatType !== 'SUPPORT'
    );

    return [...supportConversations, ...regularConversations].slice(0, MAX_RENDERED_CONVERSATIONS);
  }, [filteredConversations]);

  const calculateWhenLastMessageWasSent = (messageTimestamp) => {
    if (!messageTimestamp) return ""; 
    
    const lastMessageDate = new Date(messageTimestamp);
    const currentDate = new Date();
    const timeDifference = currentDate - lastMessageDate;
    const minutes = Math.floor(timeDifference / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 24) {
      return Math.floor(hours / 24) + "d";
    } else if (hours >= 1) {
      return hours + "h";
    } else {
      return Math.max(1, minutes) + "min";
    }
  };

  // Format booking date/time for status messages
  const formatBookingDateTimeForStatus = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return null;
    
    try {
      const start = new Date(startDateTime);
      const end = new Date(endDateTime);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return null;
      }

      // Format date as "25.11.24" (2-digit day and month)
      const day = String(start.getDate()).padStart(2, '0');
      const month = String(start.getMonth() + 1).padStart(2, '0');
      const year = start.getFullYear().toString().slice(-2);
      const dateStr = `${day}.${month}.${year}`;

      // Format time as "14-20 Uhr"
      const timeFormatter = new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      const startTimeStr = timeFormatter.format(start);
      const endTimeStr = timeFormatter.format(end);

      return {
        date: dateStr,
        time: `${startTimeStr}-${endTimeStr} Uhr`
      };
    } catch (error) {
      return null;
    }
  };

  // Get booking status with date/time for studioName area
  const getBookingStatusWithDateTime = (conversation) => {
    if (!conversation.isBookingRelated && !conversation.linkedBookingID) {
      return null;
    }

    const dateTime = formatBookingDateTimeForStatus(conversation.bookingStartDate, conversation.bookingEndDate);
    if (!dateTime) {
      return null;
    }

    // Get status badge to determine the status
    const statusBadge = getConversationStatusBadge(conversation);
    
    // Format: "Status · Date Time"
    const statusText = statusBadge || 'Anfrage';
    return `${statusText} · ${dateTime.date} ${dateTime.time}`;
  };

  const truncateMessage = (text, charactersCount) => {
    if (!text) return "";

    // Avoid DOM measurement for every row render (major source of jank on mobile).
    const maxChars = charactersCount ? Math.max(1, Math.floor(charactersCount / 6)) : 30;
    if (text.length <= maxChars) return text;
    return `${text.slice(0, maxChars).trimEnd()}...`;
  };

  useEffect(() => {
      const activeItem = listItemsRef.current[activeTab];
      const navElement = navRef.current;
        
      if (activeItem && navElement) {
          const navRect = navElement.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();
          
        setIndicatorStyle({
            left: `${itemRect.left - navRect.left}px`,
            width: `${itemRect.width}px`
        });
      }
  }, [activeTab]);

  useEffect(() => {
    const el = conversationsScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [appliedTab]);

  // const handleDeleteConversation = (e, conversationId) => {
  //   e.stopPropagation(); // Prevent triggering conversation selection
    
  //   if (window.confirm("Are you sure you want to delete this conversation?")) {
  //     setDeletingConversationId(conversationId);
      
  //     deleteChatRoomMutation({
  //       variables: {
  //         input: { id: conversationId }
  //       }
  //     });
  //   }
  // };

  return (
      <div className={`${classes.container} ${isStudioWindowOpen ? classes.open : classes.closed}`} >
          <h1 className={`${classes.desktopTitle} titleSemibold3`}>Nachrichten</h1>
          <h1 className={`${classes.phoneTitle} titleSemibold2`}>Nachrichten</h1>
          <div className={classes.searchContainer}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path d="M7.05891 12.5897C8.56207 12.5897 9.94108 12.0435 11.0097 11.1368L15.8123 15.824L15.8845 15.8945L15.9543 15.8216L16.5722 15.1772L16.6408 15.1057L16.5699 15.0364L11.7796 10.359C12.6773 9.27946 13.2178 7.88661 13.2178 6.36864C13.2178 2.93792 10.4573 0.147559 7.05891 0.147559C3.66056 0.147559 0.9 2.93792 0.9 6.36864C0.9 9.79935 3.66056 12.5897 7.05891 12.5897ZM7.15891 11.4966V11.4925H7.05891C4.26326 11.4925 1.98557 9.19295 1.98557 6.36611C1.98557 3.5394 4.26313 1.24474 7.05891 1.24474C9.85456 1.24474 12.1322 3.54433 12.1322 6.37117C12.1322 9.16422 9.90867 11.4426 7.15891 11.4966Z" fill="#77808A" stroke="#77808A" strokeWidth="0.2"/>
            </svg>
            <input 
              type="text" 
              placeholder="Suchen" 
              className={classes.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className={classes.conversationsFilterContainer}>
            <nav ref={navRef} className={classes.navigationList}>
                <li 
                  ref={el => listItemsRef.current[0] = el}
                  className={`mediumLarge ${activeTab === 0 ? classes.active : classes.inactive}`}
                  onClick={() => handleTabSwitch(0)}
                >
                  Alle
                </li>
                <li 
                  ref={el => listItemsRef.current[1] = el}
                  className={`mediumLarge ${activeTab === 1 ? classes.active : classes.inactive}`}
                  onClick={() => handleTabSwitch(1)}
                >
                  Entdecker
                </li>
                <li 
                  ref={el => listItemsRef.current[2] = el}
                  className={`mediumLarge ${activeTab === 2 ? classes.active : classes.inactive}`}
                  onClick={() => handleTabSwitch(2)}
                >
                  Creator
                </li>
            </nav>
              <div className={classes.indicatorContainer}>
                      <div 
                          className={classes.indicator} 
                          style={indicatorStyle}
                      />
                <div className={classes.horizontalLine}></div>
              </div>
          </div>
          {isLoading || (studiosIdsLoading && (appliedTab === 1 || appliedTab === 2)) ? (
            <div className={classes.loadingContainer}>
              <div className={classes.loadingSpinner}></div>
            </div>
          ) : studiosIdsError && (appliedTab === 1 || appliedTab === 2) ? (
            <div className={classes.errorContainer}>
              <div className={classes.errorContent}>
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="24" fill="#FEE2E2"/>
                  <path d="M24 12C17.373 12 12 17.373 12 24C12 30.627 17.373 36 24 36C30.627 36 36 30.627 36 24C36 17.373 30.627 12 24 12ZM26 28H22V32H26V28ZM26 16H22V26H26V16Z" fill="#DC2626"/>
                </svg>
                <h3 className="titleSemibold">Sorry, ein Fehler ist aufgetreten</h3>
                <p className="mediumLarge" style={{color: "var(--dark-grey-2)", textAlign: "center"}}>
                  Wir konnten deine Studios nicht laden. Bitte versuche es später erneut.
                </p>
                <button 
                  className={`${classes.retryButton} regularSmall`}
                  onClick={() => window.location.reload()}
                >
                  Erneut versuchen
                </button>
              </div>
            </div>
          ) : conversations.length > 0 ? (
          <div ref={conversationsScrollRef} className={classes.conversationsContainer}>
            {visibleConversations?.map((conversation) => (
              <div 
                className={`${classes.conversationItem} ${activeConversation && activeConversation?.id === conversation?.id ? classes.activeConversation : ''}`}
                key={conversation.id}
              >
                <div className={classes.conversationOverlay}></div>
                <div 
                  className={classes.conversationContent}
                  onClick={() => handleConversationClick(conversation)}
                >
                    <ConversationItemThumbnails
                      isSupport={conversation.chatType === 'SUPPORT'}
                      studioSrc={
                        conversation.chatType === 'SUPPORT'
                          ? (
                              conversation.guestID === userId
                                ? SUPPORT_LOGO_SRC
                                : (conversation.chatPartner?.profilePicture || fakeProfile)
                            )
                          : getStudioImageSrc(
                              conversation?.image,
                              conversation?.studio?.images?.[0]
                            )
                      }
                      profileSrc={
                        conversation.chatPartner?.profilePicture === null ||
                        conversation.chatPartner?.profilePicture === undefined
                          ? fakeProfile
                          : conversation.chatPartner?.profilePicture
                      }
                      classes={classes}
                    />
                  <div className={classes.conversationInfoContainer}>
                    <div className={classes.conversationInfo}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h2 className="mediumLarge">
                          {(() => {
                            const statusBadge = getConversationStatusBadge(conversation);
                            // For "Anfrage erhalten" case, show "Neue Anfrage" instead of the name
                            if (statusBadge === 'Neue Anfrage') {
                              return ( <p className={classes.newRequestBox}>Neue Anfrage</p>);
                            }
                            // For all other cases, show the regular name
                            return conversation.chatType === 'SUPPORT' && conversation.guestID === userId
                              ? 'Stu Support'
                              : (conversation.chatPartner?.firstName || 'Unbekannter Nutzer');
                          })()}
                        </h2>
                      </div>
                      <p style={{color: "var(--dark-grey-4)"}} className={`${classes.lastMessage} mediumMini`}>
                        {calculateWhenLastMessageWasSent(conversation.lastMessageAt)}
                      </p>
                      {/* <div className={classes.conversationDeleteContainer} onClick={(e) => handleDeleteWindow(conversation.id, e)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
                          <circle cx="2" cy="2" r="1.25" fill="#4A4C50"/>
                        </svg><svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
                          <circle cx="2" cy="2" r="1.25" fill="#4A4C50"/>
                        </svg>
                        <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
                          <circle cx="2" cy="2" r="1.25" fill="#4A4C50"/>
                        </svg>
                      </div> */}
                      {/* {openDeleteWindows[conversation?.id] && (
                        <button 
                          className={classes.deleteWindow} 
                          onClick={(e) => handleDeleteConversation(e, conversation.id)}
                          disabled={deletingConversationId === conversation.id || deleteLoading}
                        >
                          <div className={classes.deleteWindowContent}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                            <path d="M17.0693 3.99609C17.0691 3.80245 16.9125 3.64648 16.7188 3.64648H2.22852C2.03499 3.64669 1.87916 3.80258 1.87891 3.99609V5.33691C1.87907 5.50286 1.99378 5.64055 2.14844 5.67676H16.8301C16.9688 5.63033 17.0693 5.50138 17.0693 5.34668V3.99609ZM3.55762 16.4473C3.57705 16.6385 3.73135 16.7764 3.91895 16.7764H15.0488C15.2383 16.7764 15.3783 16.638 15.3896 16.4727L15.3906 16.4668L16.167 6.68652H2.78125L3.55762 16.4473ZM8.96875 14.1768V8.7666C8.96875 8.49051 9.19268 8.26669 9.46875 8.2666C9.74489 8.2666 9.96875 8.49046 9.96875 8.7666V14.1768C9.96864 14.4528 9.74483 14.6768 9.46875 14.6768C9.19274 14.6767 8.96886 14.4528 8.96875 14.1768ZM6.18359 8.27832C6.42426 8.25644 6.64098 8.4091 6.70801 8.63184L6.72656 8.73145L7.2168 14.1211L7.21582 14.2227C7.19 14.4536 7.005 14.642 6.76465 14.6641C6.52411 14.6859 6.30738 14.5341 6.24023 14.3115L6.2207 14.2119L5.73145 8.82227L5.73242 8.7207C5.75819 8.48954 5.94294 8.30021 6.18359 8.27832ZM12.7646 8.27832C13.0394 8.30354 13.2418 8.54741 13.2168 8.82227L12.7266 14.2119L12.708 14.3115C12.6409 14.5341 12.4242 14.6859 12.1836 14.6641C11.9086 14.639 11.7064 14.3961 11.7314 14.1211L12.2207 8.73145L12.2402 8.63184C12.3073 8.40917 12.524 8.25645 12.7646 8.27832ZM11.3984 1.57617C11.3982 1.3825 11.2426 1.22656 11.0488 1.22656H7.88867C7.69505 1.22669 7.53927 1.38257 7.53906 1.57617V2.64648H11.3984V1.57617ZM12.3984 2.64648H16.7188C17.4647 2.64648 18.0691 3.25017 18.0693 3.99609V5.34668C18.0693 5.93244 17.6958 6.42823 17.1748 6.61523L16.3877 16.5459L16.3867 16.5449C16.3358 17.2572 15.7379 17.7764 15.0488 17.7764H3.91895C3.2083 17.7764 2.6235 17.2372 2.56055 16.5312V16.5264L1.77051 6.60449C1.25083 6.41683 0.879055 5.92165 0.878906 5.33691V3.99609C0.879159 3.2503 1.48271 2.6467 2.22852 2.64648H6.53906V1.57617C6.53927 0.830287 7.14277 0.226689 7.88867 0.226562H11.0488C11.7948 0.226562 12.3982 0.830209 12.3984 1.57617V2.64648Z" fill="black"/>
                          </svg>
                          <p className="regularSmall">
                            {deletingConversationId === conversation.id ? (
                              <span className={classes.deleteLoading}>...</span>
                            ) : (
                              "Chat löschen"
                            )}
                          </p>
                          </div>
                        </button>
                      )} */}
                    </div>
                    <div className={classes.conversationMessage}>
                      {(() => {
                        // Check if there's a status badge - if so, show status message with semiboldSmall
                        const statusBadge = getConversationStatusBadge(conversation);
                        const statusMessage = getConversationStatusMessage(conversation);
                        
                        // If there's a status badge, show the status message with semiboldSmall font
                        if (statusBadge && statusMessage) {
                          return (
                            <p 
                              style={{
                                color: "var(--dark-grey-2)"
                              }} 
                              className="semiboldSmall"
                            >
                              {truncateMessage(statusMessage)}
                            </p>
                          );
                        }
                        
                        // If there's a status badge but no status message, don't show anything
                        if (statusBadge) {
                          return null;
                        }
                        
                        // Show regular message content only if there's no status badge
                        return (
                          <p 
                            style={{
                              color: "var(--dark-grey-2)"
                            }} 
                            className="regularSmall"
                          >
                            {truncateMessage(
                              conversation?.lastMessage?.content || "Keine Nachrichten"
                            )}
                          </p>
                        );
                      })()}
                      {(() => {
                          // Only show notification dot for unviewed messages
                          // viewedUpdateTrigger ensures re-render when viewed status changes
                          if (!userId || !conversation.hostID || !conversation.guestID) return null;
                          
                          // Check if conversation has unviewed messages
                          // Include viewedUpdateTrigger in the check to force re-render
                          const hasUnviewed = hasUnviewedMessages(conversation, userId);
                          if (!hasUnviewed) return null;
                          
                          // Force re-render by referencing viewedUpdateTrigger
                          void viewedUpdateTrigger;
                          
                          return (
                            <span style={{
                              backgroundColor: '#DF315A',
                              borderRadius: '50%',
                              width: '8px',
                              height: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                            </span>
                          );
                      })()}
                    </div>
                    <div className={classes.studioName}>
                      {(() => {
                        const bookingStatusWithDateTime = getBookingStatusWithDateTime(conversation);
                        const charactersCount = 250;
                        // If there's a booking status with date/time, show it (truncated if needed)
                        if (bookingStatusWithDateTime) {
                          return (
                            <p className="mediumMini" style={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}>
                              {truncateMessage(bookingStatusWithDateTime, charactersCount)}
                            </p>
                          );
                        }
                        
                        // Otherwise show studio name and last message date (regular conversation)
                        return (
                          <>
                            <p className="mediumMini">{conversation.studio?.StudioName || conversation.name || 'Unbekanntes Studio'}</p>
                            <div className={classes.insideStudioNameContainer}>
                              <p className="mediumMini">·</p>
                              <p className="mediumMini">
                                {conversation.lastMessageAt 
                                  ? new Date(conversation.lastMessageAt).toLocaleDateString('de-DE', { 
                                      day: '2-digit', 
                                      month: '2-digit',
                                      year: '2-digit'
                                    }) 
                                  : ''}
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* <div className={classes.unreadCount}>
                  {(() => {
                    // Calculate unread count for this conversation
                    if (!userId || !conversation.hostID || !conversation.guestID) return null;
                    const isHost = conversation.hostID === userId;
                    const unreadCount = isHost 
                      ? (conversation.hostUnreadCount || 0)
                      : (conversation.guestUnreadCount || 0);
                    
                    if (unreadCount <= 0) return null;
                    
                    return (
                      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
                        <circle cx="4.41176" cy="4.4977" r="4.41176" fill="#DF315A"/>
                      </svg>
                    );
                  })()}
                </div> */}
                
                {/* <button 
                  className={classes.deleteButton}
                  onClick={(e) => handleDeleteConversation(e, conversation.id)}
                  disabled={deletingConversationId === conversation.id || deleteLoading}
                >
                  {deletingConversationId === conversation.id ? (
                    <span className={classes.deleteLoading}>...</span>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M1 1L13 13M1 13L13 1" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </button> */}
                
              </div>
            ))}
          </div>

          ) : (
            <div className={classes.noMessagesContainer}>
              <div className={classes.noMessagesContent}>
                  <div className={classes.emptyBoxesContainer}>
                      <div className={classes.emptyBox}>
                          <div className={classes.emptyBoxImage}>
                              <div className={classes.insideImageContainer}>
                                  <div className={classes.innerImage}></div>
                              </div>
                          </div>
                          <div className={classes.emptyBoxText}>
                              <div className={classes.smallTextContainer}></div>
                              <div className={classes.textContainer}></div>
                          </div>
                      </div>
                      <div className={classes.emptyBox}>
                          <div className={classes.emptyBoxImage}>
                              <div className={classes.insideImageContainer}>
                                  <div className={classes.innerImage}></div>
                              </div>
                          </div>
                          <div className={classes.emptyBoxText}>
                              <div className={classes.smallTextContainer}></div>
                              <div className={classes.textContainer}></div>
                          </div>
                      </div>
                      <div className={classes.emptyBox}>
                          <div className={classes.emptyBoxImage}>
                              <div className={classes.insideImageContainer}>
                                  <div className={classes.innerImage}></div>
                              </div>
                          </div>
                          <div className={classes.emptyBoxText}>
                              <div className={classes.smallTextContainer}></div>
                              <div className={classes.textContainer}></div>
                          </div>
                      </div>
                      <div className={classes.emptyBox}>
                          <div className={classes.emptyBoxImage}>
                              <div className={classes.insideImageContainer}>
                                  <div className={classes.innerImage}></div>
                              </div>
                          </div>
                          <div className={classes.emptyBoxText}>
                              <div className={classes.smallTextContainer}></div>
                              <div className={classes.textContainer}></div>
                          </div>
                      </div>
                  </div>
                  <div className={classes.noMessagesTextContainer}>
                            <h2 className="titleSemibold">Noch keine Nachrichten</h2>
                            <p className="mediumLarge">
                                {hasStudios ? (
                                    "Verbessere dein Studio und werde gebucht"
                                ) : (
                                    "Erstelle dein Studio in wenigen Klicks und werde gebucht "
                                )}
                            </p>
                            {hasStudios ? (
                                <button className={`${classes.improveButton} regularSmall`}>Studio verbessern</button>
                            ) : (
                                <button className={`${classes.improveButton} regularSmall`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="white"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="white"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                    </svg>
                                    Studio erstellen
                                </button>
                            )}
                        </div>
              </div>
          </div>
          )}
      </div>
  );
};

export default ConversationsSelector;
